import React, { useState } from 'react'
import {
  Drawer,
  Box,
  Typography,
  TextField,
  CircularProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material'
import Button from '@mui/material/Button'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import { PlatinumProperties2 } from '../../types/carbon/models/PlatinumProperties2'
import { XSpecProperties } from '../../types/carbon/models/XSpecProperties'
import { ConfigData, MergedConfigResult } from '../../types/config'
import { PrimaryCellsSection, AdvancedCellsSection } from './settings/CellsSection'
import DecimalsSection from './settings/DecimalsSection'
import SignificanceSection from './settings/SignificanceSection'
import EdgeSection from './settings/EdgeSection'
import TitlesSection from './settings/TitlesSection'
import SpecPropertiesSection from './settings/SpecPropertiesSection'
import {
  defaultCells,
  defaultDecimals,
  defaultSignificance,
  defaultEdge,
  defaultTitles,
  defaultCorner,
  defaultSpecProperties,
} from './settings/defaults'

interface SpecSettingsDrawerProps {
  open: boolean
  onClose: () => void
  displayProperties: PlatinumProperties2
  specProperties: XSpecProperties
  onDisplayPropertiesChange: (props: PlatinumProperties2) => void
  onSpecPropertiesChange: (props: XSpecProperties) => void
  // Context config props
  mergedConfig?: MergedConfigResult | null
  loadingConfig?: boolean
  savingConfig?: boolean
  onSaveConfig?: (data: ConfigData) => Promise<void>
  /** Apply current settings and close the drawer */
  onApply?: () => void
}

const accordionSx = {
  backgroundColor: 'transparent',
  boxShadow: 'none',
  borderRadius: '0 !important',
  borderBottom: '1px solid',
  borderColor: 'divider',
  '&::before': { display: 'none' },
  '& .MuiAccordionSummary-root': { px: 3, py: 1.5 },
  '& .MuiAccordionDetails-root': { px: 3, pb: 3 },
}

const SpecSettingsDrawer: React.FC<SpecSettingsDrawerProps> = ({
  open,
  onClose,
  displayProperties,
  specProperties,
  onDisplayPropertiesChange,
  onSpecPropertiesChange,
  mergedConfig,
  loadingConfig,
  savingConfig,
  onSaveConfig,
  onApply,
}) => {
  const [expanded, setExpanded] = useState<string | false>(false)
  const [contextPrompt, setContextPrompt] = useState('')
  const [contextDirty, setContextDirty] = useState(false)

  // Sync context prompt from merged config when it loads
  const mergedPrompt = mergedConfig?.data?.table_analysis_prompt ?? ''
  React.useEffect(() => {
    setContextPrompt(mergedPrompt)
    setContextDirty(false)
  }, [mergedPrompt])

  const handleAccordion = (panel: string) => (_: React.SyntheticEvent, isExpanded: boolean) => {
    setExpanded(isExpanded ? panel : false)
  }

  const dp = displayProperties
  const cells = dp.cells || defaultCells
  const decimals = dp.decimals || defaultDecimals
  const significance = dp.significance || defaultSignificance
  const sideEdge = dp.side || defaultEdge
  const topEdge = dp.top || defaultEdge
  const titles = dp.titles || defaultTitles
  const corner = dp.corner || defaultCorner
  const sp = specProperties || defaultSpecProperties

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      variant="temporary"
      sx={{
        '& .MuiDrawer-paper': {
          width: '40%',
          minWidth: 380,
          maxWidth: 700,
          height: '100vh',
          display: 'flex',
          flexDirection: 'column',
          borderRadius: 0,
        },
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 3, py: 2.5, borderBottom: '1px solid', borderColor: 'divider' }}>
        <Typography variant="h6">Quick Settings</Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button variant="outlined" color="info" size="small" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="info"
            size="small"
            onClick={onApply || onClose}
          >
            Apply
          </Button>
        </Box>
      </Box>

      <Box sx={{ flex: 1, overflow: 'auto', display: 'flex', flexDirection: 'column' }}>
        {/* Cell Display — always visible */}
        <Box sx={{ borderBottom: '1px solid', borderColor: 'divider', px: 3, py: 3 }}>
          <Typography variant="subtitle2" sx={{ mb: 1.5 }}>Cell Display</Typography>
          <PrimaryCellsSection
            cells={cells}
            onChange={c => onDisplayPropertiesChange({ ...dp, cells: c })}
          />
        </Box>

        {/* Advanced Cell Display */}
        <Accordion expanded={expanded === 'advancedCells'} onChange={handleAccordion('advancedCells')} sx={accordionSx}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="subtitle2">Advanced Cell Display</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <AdvancedCellsSection
              cells={cells}
              onChange={c => onDisplayPropertiesChange({ ...dp, cells: c })}
            />
          </AccordionDetails>
        </Accordion>

        {/* Decimals */}
        <Accordion expanded={expanded === 'decimals'} onChange={handleAccordion('decimals')} sx={accordionSx}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="subtitle2">Decimals</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <DecimalsSection
              decimals={decimals}
              onChange={d => onDisplayPropertiesChange({ ...dp, decimals: d })}
            />
          </AccordionDetails>
        </Accordion>

        {/* Significance */}
        <Accordion expanded={expanded === 'significance'} onChange={handleAccordion('significance')} sx={accordionSx}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="subtitle2">Significance</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <SignificanceSection
              significance={significance}
              onChange={s => onDisplayPropertiesChange({ ...dp, significance: s })}
            />
          </AccordionDetails>
        </Accordion>

        {/* Row Edge */}
        <Accordion expanded={expanded === 'sideEdge'} onChange={handleAccordion('sideEdge')} sx={accordionSx}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="subtitle2">Row Settings (Sort / Hide / Labels)</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <EdgeSection
              label="Row"
              edge={sideEdge}
              onChange={e => onDisplayPropertiesChange({ ...dp, side: e })}
            />
          </AccordionDetails>
        </Accordion>

        {/* Column Edge */}
        <Accordion expanded={expanded === 'topEdge'} onChange={handleAccordion('topEdge')} sx={accordionSx}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="subtitle2">Column Settings (Sort / Hide / Labels)</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <EdgeSection
              label="Column"
              edge={topEdge}
              onChange={e => onDisplayPropertiesChange({ ...dp, top: e })}
            />
          </AccordionDetails>
        </Accordion>

        {/* Titles & Layout */}
        <Accordion expanded={expanded === 'titles'} onChange={handleAccordion('titles')} sx={accordionSx}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="subtitle2">Titles & Layout</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <TitlesSection
              titles={titles}
              corner={corner}
              description={dp.description || ''}
              onTitlesChange={t => onDisplayPropertiesChange({ ...dp, titles: t })}
              onCornerChange={c => onDisplayPropertiesChange({ ...dp, corner: c })}
              onDescriptionChange={d => onDisplayPropertiesChange({ ...dp, description: d })}
            />
          </AccordionDetails>
        </Accordion>

        {/* Advanced / Spec Properties */}
        <Accordion expanded={expanded === 'advanced'} onChange={handleAccordion('advanced')} sx={accordionSx}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="subtitle2">Advanced</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <SpecPropertiesSection
              specProperties={sp}
              onChange={onSpecPropertiesChange}
            />
          </AccordionDetails>
        </Accordion>

        {/* Context */}
        {onSaveConfig && (
          <Accordion expanded={expanded === 'context'} onChange={handleAccordion('context')} sx={accordionSx}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="subtitle2">Context</Typography>
            </AccordionSummary>
            <AccordionDetails>
              {loadingConfig ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
                  <CircularProgress size={24} />
                </Box>
              ) : (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <TextField
                    label="Table Analysis Prompt"
                    value={contextPrompt}
                    onChange={e => {
                      setContextPrompt(e.target.value)
                      setContextDirty(e.target.value !== mergedPrompt)
                    }}
                    multiline
                    rows={6}
                    fullWidth
                    placeholder="Provide context about this table to improve AI analysis..."
                    disabled={savingConfig}
                  />
                  <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                    <Button
                      variant="contained"
                      color="info"
                      size="small"
                      disabled={!contextDirty || savingConfig}
                      onClick={async () => {
                        if (!onSaveConfig) return
                        await onSaveConfig({ table_analysis_prompt: contextPrompt })
                        setContextDirty(false)
                      }}
                    >
                      {savingConfig ? 'Saving...' : 'Save Context'}
                    </Button>
                  </Box>
                </Box>
              )}
            </AccordionDetails>
          </Accordion>
        )}
      </Box>
    </Drawer>
  )
}

export default SpecSettingsDrawer
